// SPDX-FileCopyrightText: 2026 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import { useCallback, useLayoutEffect, useRef } from 'react';

export interface UseAutoScrollParams {
  selectedMd5?: string;
  currentOrder?: React.MutableRefObject<string[]>;
  page: number;
  setPage: (p: number) => void;
  pageSize?: number;
}

export default function useAutoScrollToSelected({
  selectedMd5,
  currentOrder,
  page,
  setPage,
  pageSize = 50,
}: UseAutoScrollParams) {
  const rowRefs = useRef<Map<string, HTMLElement | null>>(new Map());
  const initialAutoRef = useRef(false);

  const registerRowRef = useCallback(
    (md5?: string) => (el: HTMLElement | null) => {
      if (!md5) return;
      if (el) rowRefs.current.set(md5, el);
      else rowRefs.current.delete(md5);
    },
    []
  );

  // Wait until all DOM refs, including the scroll adapter's viewport, are set.
  // Run after every render because rows and their ordering arrive through refs.
  useLayoutEffect(() => {
    if (initialAutoRef.current || !selectedMd5) return;
    const row = rowRefs.current.get(selectedMd5);
    if (row) {
      row.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      initialAutoRef.current = true;
      return;
    }

    const idx = currentOrder?.current?.indexOf(selectedMd5) ?? -1;
    if (idx < 0) return;
    const targetPage = Math.floor(idx / pageSize) + 1;
    if (targetPage !== page) setPage(targetPage);
  });

  return {
    registerRowRef,
  };
}
